'use es6';

// For all of these functions a "bounds" variable has a `{ top, bottom, left, right }` shape
const validateAllBounds = (...allBounds) => {
  for (const bounds of allBounds) {
    if (bounds.top == null || bounds.bottom == null || bounds.left == null || bounds.right == null) {
      throw new Error('Invalid bounds, there must be top, bottom, left, and right properties');
    }
    if (bounds.top >= bounds.bottom) {
      throw new Error('Invalid bounds, top cannot be greater than or equal to the bottom');
    }
    if (bounds.left >= bounds.right) {
      throw new Error('Invalid bounds, left cannot be greater than or equal to the right');
    }
  }
};

// Trying an experiement to not use an object for a simple struct-like tile structure (for memory reasons)
const createTile = (left, top, isOutside = null) => {
  return {
    left,
    top,
    isOutside
  };
};
const trimRegionsToOverallBounds = (overallBounds, regionsToExclude) => {
  return regionsToExclude.map(region => {
    region = Object.assign({}, region);
    if (region.top < overallBounds.top) {
      region.top = overallBounds.top;
    }
    if (region.left < overallBounds.left) {
      region.left = overallBounds.left;
    }
    if (region.right > overallBounds.right) {
      region.right = overallBounds.right;
    }
    if (region.bottom > overallBounds.bottom) {
      region.bottom = overallBounds.bottom;
    }
    return region;
  });
};
const numericComparator = (a, b) => a - b;
const generateMatrixOfAllTileByEdges = (overallBounds, regionsToExclude) => {
  const setOfVerticalEdgePositions = new Set();
  const setOfHorizontalEdgePositions = new Set();
  setOfVerticalEdgePositions.add(overallBounds.left);
  setOfVerticalEdgePositions.add(overallBounds.right);
  setOfHorizontalEdgePositions.add(overallBounds.top);
  setOfHorizontalEdgePositions.add(overallBounds.bottom);
  regionsToExclude.forEach(region => {
    setOfVerticalEdgePositions.add(region.left);
    setOfVerticalEdgePositions.add(region.right);
    setOfHorizontalEdgePositions.add(region.top);
    setOfHorizontalEdgePositions.add(region.bottom);
  });
  const numColumns = setOfVerticalEdgePositions.size - 1;
  const numRows = setOfHorizontalEdgePositions.size - 1;
  const sortedVerticalEdgePositions = Array.from(setOfVerticalEdgePositions).sort(numericComparator);
  const sortedHorizontalEdgePositions = Array.from(setOfHorizontalEdgePositions).sort(numericComparator);
  const matrix = new Array(numColumns);
  for (let columnCtr = 0; columnCtr < numColumns; columnCtr++) {
    matrix[columnCtr] = new Array(numRows);
    for (let rowCtr = 0; rowCtr < numRows; rowCtr++) {
      const left = sortedVerticalEdgePositions[columnCtr];
      const top = sortedHorizontalEdgePositions[rowCtr];
      matrix[columnCtr][rowCtr] = createTile(left, top);
    }
  }
  return {
    matrix,
    numColumns,
    numRows
  };
};
const isPointWithinAnyRegion = (x, y, regions) => {
  return regions.some(region => {
    return x >= region.left && y >= region.top && x < region.right && y < region.bottom;
  });
};

// generateTilesOutsideOf()
//
// Gather vertical "strips" of rectangles that make up the "negative space" outside of array of rectangular
// regions in some document/canvas. Returns an array of rectangle bounds objects.
//
// Params:
//  - overallBounds gives the bounds of the whole document/canvas
//  - regionsToExclude is an array of rectangles representing the "positive space". All

export const generateTilesOutsideOf = (overallBounds, regionsToExclude) => {
  validateAllBounds(overallBounds, ...regionsToExclude);
  regionsToExclude = trimRegionsToOverallBounds(overallBounds, regionsToExclude);
  const {
    matrix: tileMatrix,
    numColumns,
    numRows
  } = generateMatrixOfAllTileByEdges(overallBounds, regionsToExclude);

  // Mark all tiles in the matrix that are outside of the excluded regions
  for (let columnCtr = 0; columnCtr < numColumns; columnCtr++) {
    for (let rowCtr = 0; rowCtr < numRows; rowCtr++) {
      const tile = tileMatrix[columnCtr][rowCtr];
      tile.isOutside = !isPointWithinAnyRegion(tile.left, tile.top, regionsToExclude);
    }
  }

  // Iterate down each column, connecting each of the matrix tiles that are outside and adjacent
  // to each other into a single rectangle. This is intended to be a basic and fast way to collect
  // many tiles into larger chunks, but _not_ and actually try to have the optimal/minimal number of
  // rectangles.
  const consolidatedRectangles = [];
  for (let columnCtr = 0; columnCtr < numColumns; columnCtr++) {
    let currentCombinedTile;

    // Get this column's right edge from:
    //   - the document edge if this is the last column
    //   - or the left edge of the next column
    const right = columnCtr + 1 === numColumns ? overallBounds.right : tileMatrix[columnCtr + 1][0].left;
    for (let rowCtr = 0; rowCtr < numRows; rowCtr++) {
      const tile = tileMatrix[columnCtr][rowCtr];

      // If there is no currently "open" rectangles and this tile is outside, then "open" a new rectangle
      // giving it the top, left, and right of this tile
      //
      // Otherwise f there is a currently open rectangle and we just ran into a non-exlucded tile, then
      // "close" the current rectangle, setting its bottom to the top of this tile (and push it onto
      // the result array)
      if (currentCombinedTile == null && tile.isOutside) {
        currentCombinedTile = {
          top: tile.top,
          left: tile.left,
          right
        };
      } else if (currentCombinedTile != null && !tile.isOutside) {
        currentCombinedTile.bottom = tile.top;
        consolidatedRectangles.push(currentCombinedTile);
        currentCombinedTile = null;
      }
    }

    // If the current rectangle is still "open" at the end of the column, close it with the document's bottom
    if (currentCombinedTile != null) {
      currentCombinedTile.bottom = overallBounds.bottom;
      consolidatedRectangles.push(currentCombinedTile);
    }
  }
  return consolidatedRectangles;
};

// ------------------------------------
// Some unit test visualization helpers

// If the recangle tiles outside the excluded regions were calculated correctly, this visualization
// should show:
//  - No solid red or solid blue visible
//  - Only various shades of pink and white inside the green outlines of the exluded regions
//  - Only purple outside the green outlines

export const buildTileVisualizationElement = (overallBounds, regionsToExclude, consolidatedRectangles) => {
  // Make the whole background red and outline with black
  const containerEl = document.createElement('div');
  containerEl.className = 'tile-visualization';
  containerEl.style.backgroundColor = 'red';
  containerEl.style.outline = '2px solid black';
  containerEl.style.width = '100px';
  containerEl.style.height = '100px';
  containerEl.style.position = 'relative';

  // Create a transparent blue div as each rectangle outside of the exlucded regions and absolutely
  // position them within the red container
  consolidatedRectangles.forEach(rectangle => {
    const rectangleEl = document.createElement('div');
    rectangleEl.className = 'consolided-rectangle';
    rectangleEl.style.backgroundColor = 'rgba(0,0,255,0.5)';
    rectangleEl.style.outline = '1px solid black';
    rectangleEl.style.position = 'absolute';
    rectangleEl.style.top = `${rectangle.top}px`;
    rectangleEl.style.left = `${rectangle.left}px`;
    rectangleEl.style.height = `${rectangle.bottom - rectangle.top}px`;
    rectangleEl.style.width = `${rectangle.right - rectangle.left}px`;
    containerEl.appendChild(rectangleEl);
  });

  // Create a transparent white div with yellow outline as each excluded region and absolutely
  // position it within the red container
  regionsToExclude.forEach(excludedRegion => {
    const excludedEl = document.createElement('div');
    excludedEl.className = 'excluded-region';
    excludedEl.style.backgroundColor = 'rgba(255,255,255,0.7)';
    excludedEl.style.outline = '1px solid yellow';
    excludedEl.style.position = 'absolute';
    excludedEl.style.top = `${excludedRegion.top}px`;
    excludedEl.style.left = `${excludedRegion.left}px`;
    excludedEl.style.height = `${excludedRegion.bottom - excludedRegion.top}px`;
    excludedEl.style.width = `${excludedRegion.right - excludedRegion.left}px`;
    containerEl.appendChild(excludedEl);
  });
  return containerEl;
};